/**
 * WorkspacePermissions - 워크스페이스 멤버 권한 정의
 * 
 * 3-Layer Permission Resolution Chain:
 *   Layer 1: rolePermissions[role]       ← 역할 기본값
 *   Layer 2: permissionGroups (OR 합산)   ← 그룹 권한 (Phase 3)
 *   Layer 3: overrides (명시적 덮어쓰기)   ← 개별 오버라이드 (Phase 2)
 */

import { WorkspaceMemberRole } from './Workspace';

// ── 12개 권한 항목 ──
export interface WorkspacePermissions {
    // 방(Room) 관련
    canCreateRoom: boolean;
    canDeleteOwnRoom: boolean;
    canDeleteAnyRoom: boolean;
    canManageRoomSettings: boolean;

    // 멤버 관련
    canInviteMembers: boolean;
    canRemoveMembers: boolean;
    canChangeRole: boolean;

    // 콘텐츠 관련
    canDeleteOthersMessages: boolean;
    canManageBots: boolean;
    canManageExternalShares: boolean;

    // 모듈 관리 (게시판/캘린더/파일)
    canManageBoards: boolean;     // 워크스페이스 게시판 생성/삭제/설정 변경
    canManageCalendars: boolean;  // 워크스페이스 캘린더 관리
    canManageFiles: boolean;      // 파일 관리 모듈 관리

    // 워크스페이스 관리
    canEditWorkspaceSettings: boolean;
    canViewAdminPanel: boolean;
    canAccessWorkspaceView: boolean;  // 공개 방 목록(Listed Rooms) 열람 권한. 워크스페이스 정보(About)는 모두에게 표시됨.
    canSearchMembers: boolean;        // 멤버 검색 및 1:1 대화 초대
    canCreateRoomInviteLink: boolean; // 채팅방 초대 링크 생성
}

// ── 역할별 기본 권한 정의 ──

const OWNER_PERMISSIONS: WorkspacePermissions = {
    canCreateRoom: true,
    canDeleteOwnRoom: true,
    canDeleteAnyRoom: true,
    canManageRoomSettings: true,
    canInviteMembers: true,
    canRemoveMembers: true,
    canChangeRole: true,
    canDeleteOthersMessages: true,
    canManageBots: true,
    canManageExternalShares: true,
    canManageBoards: true,
    canManageCalendars: true,
    canManageFiles: true,
    canEditWorkspaceSettings: true,
    canViewAdminPanel: true,
    canAccessWorkspaceView: true,
    canSearchMembers: true,
    canCreateRoomInviteLink: true,
};

const ADMIN_PERMISSIONS: WorkspacePermissions = {
    canCreateRoom: true,
    canDeleteOwnRoom: true,
    canDeleteAnyRoom: true,
    canManageRoomSettings: true,
    canInviteMembers: true,
    canRemoveMembers: true,
    canChangeRole: true,
    canDeleteOthersMessages: true,
    canManageBots: true,
    canManageExternalShares: true,
    canManageBoards: true,
    canManageCalendars: true,
    canManageFiles: true,
    canEditWorkspaceSettings: true,
    canViewAdminPanel: true,
    canAccessWorkspaceView: true,
    canSearchMembers: true,
    canCreateRoomInviteLink: true,
};

const MEMBER_PERMISSIONS: WorkspacePermissions = {
    canCreateRoom: true,
    canDeleteOwnRoom: true,
    canDeleteAnyRoom: false,
    canManageRoomSettings: false,
    canInviteMembers: true,
    canRemoveMembers: false,
    canChangeRole: false,
    canDeleteOthersMessages: false,
    canManageBots: false,
    canManageExternalShares: true,
    canManageBoards: false,
    canManageCalendars: false,
    canManageFiles: false,
    canEditWorkspaceSettings: false,
    canViewAdminPanel: false,
    canAccessWorkspaceView: true,
    canSearchMembers: true,
    canCreateRoomInviteLink: true,
};

const GUEST_PERMISSIONS: WorkspacePermissions = {
    canCreateRoom: false,
    canDeleteOwnRoom: false,
    canDeleteAnyRoom: false,
    canManageRoomSettings: false,
    canInviteMembers: false,
    canRemoveMembers: false,
    canChangeRole: false,
    canDeleteOthersMessages: false,
    canManageBots: false,
    canManageExternalShares: false,
    canManageBoards: false,
    canManageCalendars: false,
    canManageFiles: false,
    canEditWorkspaceSettings: false,
    canViewAdminPanel: false,
    canAccessWorkspaceView: false,
    canSearchMembers: false,
    canCreateRoomInviteLink: false,
};

// ── 역할별 기본 권한 맵 ──
export type RolePermissionsMap = Record<Exclude<WorkspaceMemberRole, 'owner'>, WorkspacePermissions>;

export const DEFAULT_ROLE_PERMISSIONS: RolePermissionsMap = {
    admin: { ...ADMIN_PERMISSIONS },
    member: { ...MEMBER_PERMISSIONS },
    guest: { ...GUEST_PERMISSIONS },
};

// ── 권한 계산 함수 (3-Layer Resolution Chain) ──

/**
 * 최종 권한을 계산합니다.
 * 
 * @param role - 멤버의 역할
 * @param rolePermsMap - 워크스페이스의 역할별 권한 템플릿 (null이면 기본값 사용)
 * @param groups - 소속 그룹들의 권한 (Phase 3용, 기본 빈 배열)
 * @param overrides - 개별 오버라이드 (Phase 2용, 기본 null)
 */
export function resolvePermissions(
    role: WorkspaceMemberRole,
    rolePermsMap: RolePermissionsMap | null,
    groups: Partial<WorkspacePermissions>[] = [],
    overrides: Partial<WorkspacePermissions> | null = null,
): WorkspacePermissions {
    // owner는 항상 모든 권한
    if (role === 'owner') {
        return { ...OWNER_PERMISSIONS };
    }

    const rMap = rolePermsMap ?? DEFAULT_ROLE_PERMISSIONS;

    // Layer 1: 역할 기본값
    const result: WorkspacePermissions = { ...(rMap[role] ?? GUEST_PERMISSIONS) };

    // Layer 2: 그룹 OR 합산 (어느 그룹이라도 true이면 true)
    for (const group of groups) {
        for (const [key, value] of Object.entries(group)) {
            if (value === true) {
                (result as any)[key] = true;
            }
        }
    }

    // Layer 3: 오버라이드 (명시적 값이 최종)
    if (overrides) {
        for (const [key, value] of Object.entries(overrides)) {
            if (value !== undefined) {
                (result as any)[key] = value;
            }
        }
    }

    return result;
}

/** 역할에 따른 기본 permissions 반환 (overrides/groups 없이) */
export function getDefaultPermissions(role: WorkspaceMemberRole, rolePermsMap?: RolePermissionsMap | null): WorkspacePermissions {
    return resolvePermissions(role, rolePermsMap ?? null);
}

/** 모든 권한 키 목록 */
export const PERMISSION_KEYS: (keyof WorkspacePermissions)[] = [
    'canCreateRoom',
    'canDeleteOwnRoom',
    'canDeleteAnyRoom',
    'canManageRoomSettings',
    'canInviteMembers',
    'canRemoveMembers',
    'canChangeRole',
    'canDeleteOthersMessages',
    'canManageBots',
    'canManageExternalShares',
    'canManageBoards',
    'canManageCalendars',
    'canManageFiles',
    'canEditWorkspaceSettings',
    'canViewAdminPanel',
    'canAccessWorkspaceView',
    'canSearchMembers',
    'canCreateRoomInviteLink',
];

/**
 * 멤버의 최종 permissions를 가져옵니다 (Lazy Resolution + 신규 필드 보완).
 * - permissions가 없으면 → role에서 기본값 계산
 * - permissions가 있으면 → role 기본값을 베이스로, 저장된 값을 덮어쓰기
 *   (새로 추가된 권한 키가 누락된 레거시 데이터도 role 기본값으로 자동 보완)
 */
export function getMemberPermissions(
    member: { role?: WorkspaceMemberRole; permissions?: WorkspacePermissions | null },
): WorkspacePermissions {
    const rolePerms = getDefaultPermissions(member.role as WorkspaceMemberRole ?? 'member');
    if (!member.permissions) return rolePerms;
    // 저장된 permissions를 role 기본값 위에 병합 (누락된 키는 role 기본값 사용)
    return { ...rolePerms, ...member.permissions };
}
